import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store.js';
import { api } from '../services/api-client.js';

const DEALER_STEPS = ['Welcome', 'Business Info', 'Connect Platforms', 'First Vehicle'];
const INDIVIDUAL_STEPS = ['Welcome', 'First Vehicle'];

const STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const dealership = useAuthStore((s) => s.dealership);
  const setAuth = useAuthStore((s) => s.setAuth);
  const user = useAuthStore((s) => s.user);
  const isDealer = useAuthStore((s) => s.isDealer);
  const dealer = isDealer();

  const STEPS = dealer ? DEALER_STEPS : INDIVIDUAL_STEPS;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Business info form (dealer only)
  const [biz, setBiz] = useState({
    name: dealership?.name || '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: 'GA',
    zip: '',
    license_number: '',
  });

  const update = (field: string, value: string) => setBiz({ ...biz, [field]: value });

  const saveBusinessInfo = async () => {
    if (!biz.name.trim()) return;
    setSaving(true);
    try {
      await api.patch('/settings/dealership', biz);
      if (dealership) {
        setAuth(token!, user!, { ...dealership, name: biz.name });
      }
    } catch (e) { console.error(e); }
    setSaving(false);
    setStep(2);
  };

  const finish = () => {
    localStorage.setItem('cdos-onboarded', '1');
    navigate('/dashboard');
  };

  // For individuals: step 0 = Welcome, step 1 = First Vehicle
  // For dealers: step 0 = Welcome, step 1 = Business Info, step 2 = Connect Platforms, step 3 = First Vehicle
  const isLastStep = step === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-surface-900 text-white flex flex-col">
      {/* Progress bar */}
      <div className="flex gap-1 px-4 pt-safe mt-4">
        {STEPS.map((_, i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-brand-400' : 'bg-surface-700'}`} />
        ))}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        {/* Step 0: Welcome (both flows) */}
        {step === 0 && (
          <div className="text-center max-w-sm animate-fadeIn">
            <h1 className="text-gradient text-3xl font-bold mb-2">Welcome to AutoCarPost</h1>
            <p className="text-surface-400 mb-8">
              {dealer
                ? 'Scan a VIN, snap photos, and post to every platform — all from one app.'
                : 'The fastest way to sell your car. Scan the VIN, snap a few photos, and you\'re live in minutes.'}
            </p>
            <button onClick={() => setStep(1)} className="w-full bg-brand-500 text-white py-4 rounded-xl font-bold text-lg">
              Get Started
            </button>
          </div>
        )}

        {/* Dealer Step 1: Business Info */}
        {dealer && step === 1 && (
          <div className="w-full max-w-sm animate-fadeIn">
            <h2 className="text-xl font-bold mb-1">Your Dealership</h2>
            <p className="text-surface-400 text-sm mb-6">Tell us about your business</p>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-surface-500 uppercase tracking-wider">Business Name *</label>
                <input value={biz.name} onChange={(e) => update('name', e.target.value)} placeholder="Acme Auto Sales" className="input w-full mt-1" />
              </div>
              <div>
                <label className="text-xs text-surface-500 uppercase tracking-wider">Phone</label>
                <input value={biz.phone} onChange={(e) => update('phone', e.target.value)} placeholder="(555) 123-4567" className="input w-full mt-1" type="tel" />
              </div>
              <div>
                <label className="text-xs text-surface-500 uppercase tracking-wider">Street Address</label>
                <input value={biz.address_line1} onChange={(e) => update('address_line1', e.target.value)} placeholder="461 Memory Lane" className="input w-full mt-1" />
              </div>
              <div>
                <label className="text-xs text-surface-500 uppercase tracking-wider">Suite / Unit</label>
                <input value={biz.address_line2} onChange={(e) => update('address_line2', e.target.value)} placeholder="Suite 120" className="input w-full mt-1" />
              </div>
              <div className="grid grid-cols-5 gap-3">
                <div className="col-span-2">
                  <label className="text-xs text-surface-500 uppercase tracking-wider">City</label>
                  <input value={biz.city} onChange={(e) => update('city', e.target.value)} placeholder="Dawsonville" className="input w-full mt-1" />
                </div>
                <div className="col-span-1">
                  <label className="text-xs text-surface-500 uppercase tracking-wider">State</label>
                  <select value={biz.state} onChange={(e) => update('state', e.target.value)} className="input w-full mt-1 px-2">
                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-surface-500 uppercase tracking-wider">ZIP Code</label>
                  <input value={biz.zip} onChange={(e) => update('zip', e.target.value)} placeholder="30534" className="input w-full mt-1" inputMode="numeric" maxLength={10} />
                </div>
              </div>
              <div>
                <label className="text-xs text-surface-500 uppercase tracking-wider">Dealer License #</label>
                <input value={biz.license_number} onChange={(e) => update('license_number', e.target.value)} placeholder="Optional" className="input w-full mt-1" />
              </div>
            </div>
            <button onClick={saveBusinessInfo} disabled={saving || !biz.name.trim()} className="w-full bg-brand-500 text-white py-4 rounded-xl font-bold text-lg mt-6 disabled:opacity-50">
              {saving ? 'Saving...' : 'Continue'}
            </button>
            <button onClick={() => setStep(2)} className="w-full text-surface-500 text-sm py-3 mt-2">
              Skip for now
            </button>
          </div>
        )}

        {/* Dealer Step 2: Connect Platforms */}
        {dealer && step === 2 && (
          <div className="w-full max-w-sm animate-fadeIn">
            <h2 className="text-xl font-bold mb-1">Connect Platforms</h2>
            <p className="text-surface-400 text-sm mb-6">Auto-post vehicles to marketplaces</p>
            <div className="space-y-3">
              {[
                { name: 'Facebook Marketplace', desc: 'Auto-post to your FB page', icon: 'f' },
                { name: 'Craigslist', desc: 'Bulk posting via 3rd party', icon: 'C' },
                { name: 'TikTok', desc: 'Short video posts', icon: 'T' },
                { name: 'Instagram', desc: 'Photo carousel posts', icon: 'I' },
              ].map((p) => (
                <button key={p.name} onClick={() => navigate('/marketplace')} className="card w-full p-4 flex items-center gap-4 text-left hover:bg-surface-700 transition-colors">
                  <div className="w-10 h-10 bg-surface-700 rounded-lg flex items-center justify-center text-lg font-bold text-brand-400">{p.icon}</div>
                  <div className="flex-1">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-surface-500">{p.desc}</div>
                  </div>
                  <span className="text-surface-600">+</span>
                </button>
              ))}
            </div>
            <button onClick={() => setStep(3)} className="w-full bg-brand-500 text-white py-4 rounded-xl font-bold text-lg mt-6">
              Continue
            </button>
            <button onClick={() => setStep(3)} className="w-full text-surface-500 text-sm py-3 mt-2">
              Connect later
            </button>
          </div>
        )}

        {/* Last Step: First Vehicle (both flows) */}
        {isLastStep && (
          <div className="text-center max-w-sm animate-fadeIn">
            <div className="text-5xl mb-6">+</div>
            <h2 className="text-xl font-bold mb-2">
              {dealer ? 'Add Your First Vehicle' : 'List Your Car'}
            </h2>
            <p className="text-surface-400 text-sm mb-8">
              {dealer
                ? "Scan a VIN barcode, snap photos, enter miles. That's it. We auto-decode, auto-price, and auto-post."
                : "Scan your VIN, take a few photos, enter the miles. We'll decode everything and create your listing."}
            </p>
            <button onClick={() => { finish(); navigate('/add'); }} className="w-full bg-brand-500 text-white py-4 rounded-xl font-bold text-lg">
              {dealer ? 'Scan First VIN' : 'Sell My Car'}
            </button>
            <button onClick={finish} className="w-full text-surface-500 text-sm py-3 mt-2">
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
